<template>
  <div class="page-wrapper">
    <nav-header />
    
    <div class="kb-container">
      <div class="kb-form-wrapper">
        <div class="form-header">
          <h2>{{ isEdit ? '编辑知识库' : '创建知识库' }}</h2>
          <p class="desc">完善知识库信息,开始你的知识分享之旅</p>
        </div>

        <el-form 
          ref="form" 
          :model="formData" 
          :rules="rules"
          label-width="100px"
          class="kb-form"
        >
          <!-- 基本信息 -->
          <div class="form-section">
            <h3 class="section-title">基本信息</h3>
            
            <el-form-item label="封面图片" prop="coverImage">
              <el-upload
                class="cover-uploader"
                :action="action"
                :show-file-list="false"
                :on-success="handleCoverSuccess"
                :before-upload="beforeCoverUpload"
              >
                <img v-if="formData.coverImage" :src="formData.coverImage" class="cover-image">
                <i v-else class="el-icon-plus cover-uploader-icon"></i>
              </el-upload>
              <div class="upload-tip">建议尺寸: 800x400px, 支持 jpg、png 格式</div>
            </el-form-item>

            <el-form-item label="知识库名称" prop="name">
              <el-input v-model="formData.name" placeholder="请输入知识库名称"></el-input>
            </el-form-item>

            <el-form-item label="知识库描述" prop="description">
              <el-input 
                type="textarea" 
                v-model="formData.description" 
                :rows="4"
                placeholder="请输入知识库描述"
              ></el-input>
            </el-form-item>

            <el-form-item label="所属分类" prop="categoryId">
              <el-select v-model="formData.categoryId" placeholder="请选择分类">
                <el-option
                  v-for="item in categories"
                  :key="item.dictValue"
                  :label="item.dictLabel"
                  :value="item.dictValue"
                >
                </el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="标签" prop="tags">
              <el-tag
                :key="tag"
                v-for="tag in formData.tags"
                closable
                :disable-transitions="false"
                @close="handleTagClose(tag)"
                class="tag-item"
              >
                {{tag}}
              </el-tag>
              <el-input
                class="input-new-tag"
                v-if="inputVisible"
                v-model="inputValue"
                ref="saveTagInput"
                size="small"
                @keyup.enter.native="handleInputConfirm"
                @blur="handleInputConfirm"
              >
              </el-input>
              <el-button v-else class="button-new-tag" size="small" @click="showInput">
                + 新增标签
              </el-button>
              <div class="tag-tip">最多可添加5个标签,每个标签不超过10个字符</div>
            </el-form-item>
          </div>

          <!-- 访问设置 -->
          <div class="form-section">
            <h3 class="section-title">访问设置</h3>
            
            <el-form-item label="访问权限" prop="accessType">
              <el-radio-group v-model="formData.accessType">
                <el-radio :label="0">公开</el-radio>
                <el-radio :label="1">私有</el-radio>
                <el-radio :label="2">付费</el-radio>
              </el-radio-group>
            </el-form-item>

            <template v-if="formData.accessType === 2">
              <el-form-item label="价格" prop="price">
                <el-input-number 
                  v-model="formData.price"
                  :precision="2"
                  :step="0.01"
                  :min="0"
                ></el-input-number>
                <span class="unit">元</span>
              </el-form-item>

              <el-form-item label="有效期" prop="validDays">
                <el-input-number 
                  v-model="formData.validDays"
                  :min="1"
                  :step="1"
                ></el-input-number>
                <span class="unit">天</span>
              </el-form-item>
            </template>
          </div>

          <!-- 表单操作 -->
          <div class="form-actions">
            <el-button @click="cancel">取消</el-button>
            <el-button type="primary" @click="submitForm" :loading="submitting">
              {{ isEdit ? '保存修改' : '创建知识库' }}
            </el-button>
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import NavHeader from '@/components/layout/NavHeader.vue'
import { addKnowledgeBase, updateKnowledgeBase, getKnowledgeBase } from '@/api/knowledgeBase/knowledgeBase'
import { getDictList } from '@/api/system/dict'

export default {
  name: 'KnowledgeBaseEdit',
  
  components: {
    NavHeader
  },

  data() {
    return {
      action: process.env["VUE_APP_BASE_API2"]+'/system/image/upload',
      isEdit: false,
      submitting: false,
      categories: [], // 分类列表
      inputVisible: false,
      inputValue: '',
      formData: {
        name: '',
        description: '',
        coverImage: '',
        categoryId: '',
        accessType: 0,
        price: 0,
        validDays: 365,
        status: 1,
        tags: []
      },
      rules: {
        name: [
          { required: true, message: '请输入知识库名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        description: [
          { required: true, message: '请输入知识库描述', trigger: 'blur' },
          { min: 10, max: 500, message: '长度在 10 到 500 个字符', trigger: 'blur' }
        ],
        categoryId: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        price: [
          { required: true, message: '请输入价格', trigger: 'blur' }
        ],
        validDays: [
          { required: true, message: '请输入有效期', trigger: 'blur' }
        ],
        tags: [
          { 
            type: 'array', 
            max: 5, 
            message: '最多添加5个标签', 
            trigger: 'change'
          }
        ]
      }
    }
  },

  created() {
    this.getCategories()
    this.isEdit = !!this.$route.params.id
    if (this.isEdit) {
      this.getDetail()
    }
  },

  methods: {
    // 获取分类列表
    async getCategories() {
      try {
        const res = await getDictList('kb_category')
        // 处理返回的分类数据
        this.categories = res.rows.map(item => ({
          dictValue: parseInt(item.dictValue),
          dictLabel: item.dictLabel
        }))
      } catch (error) {
        console.error('获取分类列表失败:', error)
        this.$message.error('获取分类列表失败')
      }
    },

    // 获取知识库详情
    async getDetail() {
      try {
        const res = await getKnowledgeBase(this.$route.params.id)
        this.formData = {
          ...res.data,
          tags: res.data.tags ? res.data.tags.split(',') : []
        }
      } catch (error) {
        console.error('获取知识库详情失败:', error)
        this.$message.error('获取知识库详情失败')
      }
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          this.submitting = true
          try {
            // 将标签数组转换为字符串
            const formData = {
              ...this.formData,
              tags: this.formData.tags.join(',')
            }
            
            if (this.isEdit) {
              await updateKnowledgeBase(formData)
            } else {
              await addKnowledgeBase(formData)
            }
            
            this.$message.success(this.isEdit ? '修改成功' : '创建成功')
            this.$router.push('/knowledgeBase/my')
          } catch (error) {
            console.error(error)
            this.$message.error(this.isEdit ? '修改失败' : '创建失败')
          } finally {
            this.submitting = false
          }
        }
      })
    },

    // 取消
    cancel() {
      this.$router.back()
    },

    // 封面上传成功
    handleCoverSuccess(res, file) {
      this.formData.coverImage = res.data.originUrl
    },

    // 上传前校验
    beforeCoverUpload(file) {
      const isJPGOrPNG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 5

      if (!isJPGOrPNG) {
        this.$message.error('只能上传 JPG 或 PNG 格式的图片!')
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 5MB!')
      }
      return isJPGOrPNG && isLt2M
    },

    // 显示标签输入框
    showInput() {
      this.inputVisible = true
      this.$nextTick(_ => {
        this.$refs.saveTagInput.$refs.input.focus()
      })
    },

    // 处理标签输入确认
    handleInputConfirm() {
      let inputValue = this.inputValue.trim()
      if (inputValue) {
        if (inputValue.length > 10) {
          this.$message.warning('标签不能超过10个字符')
          return
        }
        if (this.formData.tags.length >= 5) {
          this.$message.warning('最多添加5个标签')
          return
        }
        if (this.formData.tags.includes(inputValue)) {
          this.$message.warning('标签已存在')
          return
        }
        this.formData.tags.push(inputValue)
      }
      this.inputVisible = false
      this.inputValue = ''
    },

    // 删除标签
    handleTagClose(tag) {
      this.formData.tags.splice(this.formData.tags.indexOf(tag), 1)
    }
  }
}
</script>

<style lang="scss" scoped>
.page-wrapper {
  min-height: 100vh;
  background: #f6f8fa;
}

.kb-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px;
}

.kb-form-wrapper {
  background: #fff;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.form-header {
  text-align: center;
  margin-bottom: 40px;
  
  h2 {
    font-size: 24px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }
  
  .desc {
    color: #666;
    font-size: 14px;
  }
}

.form-section {
  margin-bottom: 32px;
  
  .section-title {
    font-size: 18px;
    font-weight: 500;
    color: #333;
    margin-bottom: 24px;
    padding-left: 10px;
    border-left: 4px solid #409EFF;
  }
}

.cover-uploader {
  :deep(.el-upload) {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    width: 400px;
    height: 200px;
    
    &:hover {
      border-color: #409EFF;
    }
  }
  
  .cover-image {
    width: 400px;
    height: 200px;
    display: block;
    object-fit: cover;
  }
  
  .cover-uploader-icon {
    font-size: 28px;
    color: #8c939d;
    width: 400px;
    height: 200px;
    line-height: 200px;
    text-align: center;
  }
}

.upload-tip {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}

.unit {
  margin-left: 8px;
  color: #666;
}

.form-actions {
  margin-top: 40px;
  text-align: center;
  
  .el-button {
    min-width: 120px;
    margin: 0 10px;
  }
}

// 响应式布局
@media screen and (max-width: 768px) {
  .kb-container {
    padding: 16px;
  }
  
  .kb-form-wrapper {
    padding: 20px;
  }
  
  .cover-uploader {
    :deep(.el-upload) {
      width: 100%;
      height: 160px;
    }
    
    .cover-image {
      width: 100%;
      height: 160px;
    }
    
    .cover-uploader-icon {
      width: 100%;
      height: 160px;
      line-height: 160px;
    }
  }
}

.tag-item {
  margin-right: 10px;
  margin-bottom: 10px;
}

.button-new-tag {
  margin-bottom: 10px;
  height: 32px;
  line-height: 30px;
  padding-top: 0;
  padding-bottom: 0;
}

.input-new-tag {
  width: 90px;
  margin-right: 10px;
  vertical-align: bottom;
}

.tag-tip {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}
</style> 